import { Pic } from "./Pic";

import { useState } from "react";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import { Reveal } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";

const askHouse = (message) => {
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Casa in legno", message } }));
  scrollToId(window.innerWidth < 1024 ? "#quote-form" : "#contatti", -110);
};

const ROWS = [
  ["Parete", "Telaio di montanti + pannelli + isolante", "Pannello unico massiccio, senza giunzioni"],
  ["Ponti termici", "Da curare in corrispondenza di ogni montante", "Assenti: involucro continuo"],
  ["Comportamento sismico", "Buono", "Eccellente: struttura scatolare monolitica"],
  ["Prestazioni termiche", "Buone, legate alla posa degli strati", "Superiori, con massa termica e tenuta all'aria"],
  ["Costi di produzione", "Più lavorazioni e più fasi in cantiere", "Notevolmente ridotti: meno pezzi, meno ore"],
];

const FRAME_IMGS = [
  ["/img/emma/telaio-sezione-casa.jpg", "Casa a telaio (Platform Frame) vista in sezione"],
  ["/img/emma/telaio-sezione-tecnica.jpg", "Sezione tecnica di una casa a telaio con tutti gli strati"],
  ["/img/emma/telaio-parete.jpg", "Stratigrafia della parete a telaio"],
];

const XLAM_IMGS = [
  ["/img/emma/xlam-sezione-casa.jpg", "Casa in X-LAM Emma vista in sezione"],
  ["/img/emma/xlam-parete-ventilata.jpg", "Parete X-LAM con facciata ventilata e rivestimento in legno"],
  ["/img/emma/xlam-parete-cappotto.jpg", "Parete X-LAM con cappotto isolante e intonaco"],
];

const Gallery = ({ imgs, id, tkey, dark }) => {
  const t = useT();
  const [cur, setCur] = useState(0);
  return (
    <div data-testid={`${id}-gallery`}>
      <a href={imgs[cur][0]} target="_blank" rel="noreferrer" className="relative block aspect-video overflow-hidden bg-white" data-testid={`${id}-gallery-main`}>
        {imgs.map(([src, alt], i) => (
          <Pic key={src} src={src} alt={t(`${tkey}${i}`, alt)} loading="lazy" className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${i === cur ? "opacity-100" : "opacity-0"}`} />
        ))}
      </a>
      <p className={`mt-3 text-sm ${dark ? "text-[#CFC8BC]" : "text-[#6E685E]"}`} data-testid={`${id}-gallery-caption`}>{t(`${tkey}${cur}`, imgs[cur][1])}</p>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {imgs.map(([src, alt], i) => (
          <button key={src} type="button" onClick={() => setCur(i)} aria-label={t(`${tkey}${i}`, alt)} className={`aspect-video overflow-hidden border-2 bg-white transition-[border-color,opacity] duration-300 ${i === cur ? "border-[#D98A52]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`${id}-gallery-thumb-${i}`}>
            <Pic src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const SystemCard = ({ dark, tag, title, text, wall, rows, cta, onCta, testid }) => (
  <div className={`flex flex-col p-6 sm:p-10 ${dark ? "bg-[#121110] text-[#F7F5F0] lg:-my-6" : "border border-[#121110]/15 bg-[#EFECE6] text-[#121110]"}`} data-testid={testid}>
    <span className={`w-fit rounded-full px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em] ${dark ? "bg-[#C26D38] text-[#121110]" : "border border-[#121110]/20 text-[#6E685E]"}`}>{tag}</span>
    <h3 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h3>
    <p className={`mt-4 text-base leading-relaxed ${dark ? "text-[#CFC8BC]" : "text-[#6E685E]"}`}>{text}</p>
    <div className="mt-8">{wall}</div>
    <ul className="mt-8 flex-1 space-y-4">
      {rows.map(([k, v], i) => (
        <li key={k} className={`flex gap-3 border-t pt-4 text-sm ${dark ? "border-white/15" : "border-[#121110]/15"}`} data-testid={`${testid}-row-${i}`}>
          {dark ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D98A52]" /> : <Minus className="mt-0.5 h-4 w-4 shrink-0 text-[#6E685E]" />}
          <span><span className={`block font-mono text-xs uppercase tracking-[0.18em] ${dark ? "text-[#A39E93]" : "text-[#6E685E]"}`}>{k}</span><span className="mt-1 block">{v}</span></span>
        </li>
      ))}
    </ul>
    <button onClick={onCta} className={`mt-10 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 ${dark ? "bg-[#C26D38] text-[#121110] hover:bg-[#D98A52]" : "border border-[#121110]/25 hover:bg-[#121110]/5"}`} data-testid={`${testid}-cta`}>
      {cta} <ArrowUpRight className="h-4 w-4" />
    </button>
  </div>
);

export const HouseSystems = () => {
  const t = useT();
  const rows = (col) => ROWS.map((r, i) => [t(`sys.k${i}`, r[0]), t(`sys.${col}${i}`, r[col === "f" ? 1 : 2])]);
  return (
    <div id="sistemi" className="mt-20 scroll-mt-28" data-testid="house-systems">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C26D38]">{t("sys.label", "Due sistemi costruttivi")}</p>
        <h3 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          {t("sys.h", "Telaio o X-LAM? Ti spieghiamo perché noi consigliamo")} <em className="font-serif font-normal text-[#C26D38]">{t("sys.hem", "l'X-LAM.")}</em>
        </h3>
      </Reveal>
      <div className="mt-12 grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
        <Reveal>
          <SystemCard
            tag={t("sys.f.tag", "Sistema collaudato")}
            title={t("sys.f.title", "Sistema Platform Frame")}
            text={t("sys.f.text", "La classica casa a telaio: struttura leggera di montanti e traversi, chiusa da pannelli e riempita di isolante. Una soluzione valida e diffusa, ma la parete nasce dall'unione di tanti elementi diversi.")}
            wall={<Gallery imgs={FRAME_IMGS} id="frame" tkey="sys.fimg" />}
            rows={rows("f")}
            cta={t("sys.f.cta", "Info sul telaio")}
            onCta={() => askHouse(t("sys.f.msg", "Vorrei informazioni sul Sistema Platform Frame."))}
            testid="system-frame"
          />
        </Reveal>
        <Reveal delay={0.15}>
          <SystemCard
            dark
            tag={t("sys.x.tag", "Consigliato da Emma")}
            title={t("sys.x.title", "Sistema X-LAM Emma")}
            text={t("sys.x.text", "Pareti uniche in legno massiccio a strati incrociati, prodotte nel nostro stabilimento: nessuna giunzione, nessun ponte termico. Meno lavorazioni significa costi di produzione e prezzi notevolmente ridotti, con prestazioni antisismiche e termiche superiori.")}
            wall={<Gallery imgs={XLAM_IMGS} id="xlam" tkey="sys.img" dark />}
            rows={rows("x")}
            cta={t("sys.x.cta", "Preventivo casa X-LAM")}
            onCta={() => askHouse(t("sys.x.msg", "Vorrei un preventivo per una casa con Sistema X-LAM Emma."))}
            testid="system-xlam"
          />
        </Reveal>
      </div>
      <p className="mt-10 text-xs text-[#6E685E]">{t("sys.legend2", "Confronta le immagini: nel telaio la parete è fatta di tanti montanti con l'isolante inserito tra uno e l'altro; nell'X-LAM il pannello portante è un unico elemento continuo, avvolto dall'isolamento senza interruzioni.")}</p>
      <Reveal className="mt-20 grid gap-10 border-t-2 border-[#121110] pt-12 lg:grid-cols-12" data-testid="experience-block">
        <div className="lg:col-span-6">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C26D38]">{t("exp.label", "Esperienza esecutiva")}</p>
          <h3 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {t("exp.h", "Il vero risparmio non è la casa che costa meno.")} <em className="font-serif font-normal text-[#C26D38]">{t("exp.hem", "È la casa fatta bene.")}</em>
          </h3>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-[#3D3833] sm:text-lg lg:col-span-5 lg:col-start-8">
          <p>{t("exp.p1", "Inseguire il prezzo più basso spesso costa caro: errori di progetto, dettagli sbagliati in cantiere, ponti termici, varianti e manutenzioni che arrivano dopo.")}</p>
          <p>{t("exp.p2", "Il risparmio vero nasce quando progettazione e realizzazione sono affidate a chi conosce davvero la materia da anni. A chi ha sbagliato, ha capito e ha migliorato, con tanti fallimenti sulle spalle trasformati in lezioni.")}</p>
          <p className="font-semibold text-[#121110]">{t("exp.p3", "Oggi tutto questo ha un nome: esperienza esecutiva. Ed è la cosa che mettiamo in ogni casa Emma.")}</p>
        </div>
      </Reveal>
    </div>
  );
};
